import React from 'react';
import PropTypes from 'prop-types';
import { CalendarClock, Check, Clock, FileText, MessageSquare } from 'lucide-react';

// Ce qu'on accepte, montré tel que ce sera après l'action (demande de Rémy, 2026-10-09 : « le même genre de
// confirmation pour tout ») : le créneau dans l'agenda du jour, les heures de la journée, la tâche ou l'objectif à sa
// place dans sa liste, la fiche champ par champ, la note ou le message tels qu'ils seront écrits. Les aperçus sont
// calculés par le backend (nexus/previews.py), un `kind` par forme.

const HOUR = 60;
const time = (iso) => new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' });
const minutesOf = (iso) => {
  const parts = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: 'Europe/Paris' })
    .formatToParts(new Date(iso));
  const get = (type) => Number(parts.find((p) => p.type === type)?.value || 0);
  return get('hour') * HOUR + get('minute');
};
const hours = (value) => `${Number(value || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} h`;

const Frame = ({ icon: Icon = null, heading, children }) => (
  <div className="rounded-xl border border-slate-200 bg-white">
    {heading && (
      <p className="flex items-center gap-1.5 border-b border-slate-100 px-3 py-2 text-xs font-semibold text-slate-600">
        {Icon && <Icon className="h-3.5 w-3.5 shrink-0 text-indigo-500" />} <span className="min-w-0 truncate">{heading}</span>
      </p>
    )}
    {children}
  </div>
);

Frame.propTypes = { icon: PropTypes.elementType, heading: PropTypes.string, children: PropTypes.node };

/** La journée telle qu'elle est (événements en gris) et le créneau que Nexus propose (en couleur). */
function AgendaPreview({ preview }) {
  const slot = { start: minutesOf(preview.start), end: minutesOf(preview.end) };
  // Les événements marqués « disponible » (lieu de travail…) ne bloquent rien : ils ne sont pas montrés.
  const events = (preview.day || []).filter((e) => !e.free)
    .map((e) => ({ ...e, from: minutesOf(e.start), to: minutesOf(e.end) })).filter((e) => e.to > e.from);
  const first = Math.min(8 * HOUR, slot.start - HOUR, ...events.map((e) => e.from));
  const last = Math.max(19 * HOUR, slot.end + HOUR, ...events.map((e) => e.to));
  const top = Math.max(0, Math.floor(first / HOUR) * HOUR);
  const bottom = Math.min(24 * HOUR, Math.ceil(last / HOUR) * HOUR);
  const scale = Math.max(0.32, Math.min(0.6, 250 / (bottom - top))); // la journée tient sans défiler
  const y = (m) => (m - top) * scale;
  const marks = [];
  for (let h = top; h <= bottom; h += HOUR) marks.push(h);
  const day = new Date(preview.start).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Europe/Paris' });
  return (
    <Frame icon={CalendarClock} heading={day.charAt(0).toUpperCase() + day.slice(1)}>
      <div className="px-2 py-2">
        <div className="relative ml-11" style={{ height: y(bottom) + 4 }}>
          {marks.map((h) => (
            <div key={h} className="absolute inset-x-0 border-t border-slate-100" style={{ top: y(h) }}>
              <span className="absolute -left-11 -top-2 w-9 text-right text-[10px] text-slate-400">{String(h / HOUR).padStart(2, '0')}:00</span>
            </div>
          ))}
          {events.map((e, i) => (
            <div key={i} title={`${e.title} · ${time(e.start)}–${time(e.end)}`}
              className="absolute left-0 right-1/3 overflow-hidden rounded-md bg-slate-200/80 px-1.5 text-[10.5px] leading-tight text-slate-600"
              style={{ top: y(e.from) + 1, height: Math.max(14, y(e.to) - y(e.from) - 2) }}>
              {e.title}
            </div>
          ))}
          <div className="absolute left-6 right-0 truncate rounded-md bg-indigo-600 px-2 py-0.5 text-[11px] font-semibold leading-tight text-white shadow-md ring-2 ring-indigo-200"
            style={{ top: y(slot.start) + 1, height: Math.max(18, y(slot.end) - y(slot.start) - 2) }}>
            {preview.title} · {time(preview.start)}–{time(preview.end)}
            {preview.invites?.length > 0 && <span className="font-normal text-indigo-100"> · avec {preview.invites.length} invité{preview.invites.length > 1 ? 's' : ''}</span>}
          </div>
        </div>
      </div>
    </Frame>
  );
}

AgendaPreview.propTypes = { preview: PropTypes.object.isRequired };

/** Chaque jour touché, projet par projet, tel qu'il sera ; ce qui change en couleur, l'ancienne valeur barrée. */
function HoursPreview({ preview }) {
  const days = preview.days || [];
  if (!days.length) return null;
  return (
    <div className="space-y-2">
      {days.map((day) => {
        const total = Math.max(day.after, 1);
        return (
          <Frame key={day.date} icon={Clock} heading={day.label.charAt(0).toUpperCase() + day.label.slice(1)}>
            <div className="space-y-2 px-3 py-2">
              <div className="flex h-2 overflow-hidden rounded-full bg-slate-100" title={`${hours(day.after)} ce jour-là`}>
                {day.rows.filter((r) => r.after > 0).map((r) => (
                  <div key={r.label} className={r.changed ? 'bg-indigo-500' : 'bg-slate-300'}
                    style={{ width: `${(100 * r.after) / Math.max(total, 7)}%` }} />
                ))}
              </div>
              <ul className="space-y-1 text-sm">
                {day.rows.map((r) => (
                  <li key={r.label} className={`flex items-center justify-between gap-3 rounded-md px-2 py-1 ${r.changed ? 'bg-indigo-50 text-slate-900' : 'text-slate-500'}`}>
                    <span className="min-w-0 truncate">{r.label}</span>
                    <span className="shrink-0 tabular-nums">
                      {r.changed && <s className="mr-1.5 text-xs text-slate-400">{hours(r.before)}</s>}
                      <span className={r.changed ? 'font-semibold text-indigo-700' : ''}>{r.after ? hours(r.after) : 'retiré'}</span>
                    </span>
                  </li>
                ))}
              </ul>
              <p className="flex justify-between border-t border-slate-100 pt-1.5 text-xs text-slate-500">
                <span>Total de la journée</span>
                <span className="tabular-nums">
                  {day.before !== day.after && <s className="mr-1.5 text-slate-400">{hours(day.before)}</s>}
                  <strong className="text-slate-800">{hours(day.after)}</strong>
                </span>
              </p>
            </div>
          </Frame>
        );
      })}
    </div>
  );
}

HoursPreview.propTypes = { preview: PropTypes.object.isRequired };

const STATE = {
  new: { row: 'bg-emerald-50 ring-1 ring-emerald-200', tag: 'bg-emerald-100 text-emerald-700', label: 'Nouveau' },
  changed: { row: 'bg-indigo-50 ring-1 ring-indigo-200', tag: 'bg-indigo-100 text-indigo-700', label: 'Modifié' },
  removed: { row: 'bg-rose-50 ring-1 ring-rose-200', tag: 'bg-rose-100 text-rose-700', label: 'Retiré' },
};

const Changes = ({ changes }) => (
  <ul className="mt-1 space-y-0.5 text-xs text-slate-600">
    {changes.map((c) => (
      <li key={c.label}>
        {c.label} : {c.before !== null && c.before !== undefined && <><s className="text-slate-400">{c.before}</s> → </>}
        <strong className="text-slate-800">{c.after}</strong>
      </li>
    ))}
  </ul>
);

Changes.propTypes = { changes: PropTypes.array.isRequired };

/** La liste où l'élément prend place (tâches de l'objectif, objectifs du sprint, équipe…), l'élément en évidence. */
function ListPreview({ preview }) {
  return (
    <Frame icon={FileText} heading={preview.heading}>
      <ul className="space-y-1 px-2 py-2">
        {(preview.items || []).map((item, index) => {
          const state = STATE[item.state];
          return (
            <li key={index} className={`rounded-lg px-2 py-1.5 ${state ? state.row : ''}`}>
              <div className="flex items-start gap-2">
                <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${item.done ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-slate-300'}`}>
                  {item.done && <Check className="h-3 w-3" />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm leading-snug ${state ? 'font-semibold text-slate-900' : 'text-slate-600'} ${item.state === 'removed' || (item.done && !state) ? 'line-through decoration-slate-400' : ''}`}>
                    {item.label}
                  </p>
                  {item.meta?.length > 0 && <p className="text-xs text-slate-500">{item.meta.join(' · ')}</p>}
                  {item.changes?.length > 0 && <Changes changes={item.changes} />}
                </div>
                {state && <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${state.tag}`}>{state.label}</span>}
              </div>
            </li>
          );
        })}
      </ul>
      {preview.more > 0 && <p className="px-4 pb-2 text-xs text-slate-400">+ {preview.more} autre{preview.more > 1 ? 's' : ''} non montré{preview.more > 1 ? 's' : ''}</p>}
    </Frame>
  );
}

ListPreview.propTypes = { preview: PropTypes.object.isRequired };

/** Une fiche, champ par champ : l'ancienne valeur barrée, la nouvelle en gras. */
function ChangesPreview({ preview }) {
  return (
    <Frame icon={FileText} heading={preview.heading}>
      <dl className="divide-y divide-slate-100 px-3 py-1 text-sm">
        {(preview.fields || []).map((field) => (
          <div key={field.label} className="flex gap-3 py-1.5">
            <dt className="w-24 shrink-0 text-xs text-slate-500">{field.label}</dt>
            <dd className="min-w-0 flex-1">
              {field.before !== null && field.before !== undefined && <s className="mr-1.5 text-slate-400">{field.before}</s>}
              <strong className="text-slate-900">{field.after}</strong>
            </dd>
          </div>
        ))}
      </dl>
    </Frame>
  );
}

ChangesPreview.propTypes = { preview: PropTypes.object.isRequired };

/** Une note telle qu'elle sera écrite ; la version qu'elle remplace, repliée. */
function TextPreview({ preview }) {
  return (
    <Frame icon={FileText} heading={preview.heading}>
      <div className="max-h-60 overflow-y-auto whitespace-pre-wrap px-3 py-2 text-sm text-slate-800">
        {preview.content}{preview.cut && <span className="text-slate-400"> […]</span>}
      </div>
      {preview.before && (
        <details className="border-t border-slate-100 px-3 py-2 text-xs text-slate-500">
          <summary className="cursor-pointer">Version actuelle (remplacée)</summary>
          <div className="mt-1 max-h-40 overflow-y-auto whitespace-pre-wrap text-slate-500">{preview.before}</div>
        </details>
      )}
    </Frame>
  );
}

TextPreview.propTypes = { preview: PropTypes.object.isRequired };

/** Un message Slack tel qu'il partira. */
function MessagePreview({ preview }) {
  return (
    <Frame icon={MessageSquare} heading={`À ${preview.to}${preview.thread ? ' · dans le fil' : ''}`}>
      <div className="px-3 py-2">
        <div className="max-h-60 overflow-y-auto whitespace-pre-wrap rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-800">
          {preview.text}{preview.cut && <span className="text-slate-400"> […]</span>}
        </div>
      </div>
    </Frame>
  );
}

MessagePreview.propTypes = { preview: PropTypes.object.isRequired };

const VIEWS = {
  agenda: AgendaPreview, hours: HoursPreview, list: ListPreview, changes: ChangesPreview, text: TextPreview,
  message: MessagePreview,
};

/** L'aperçu d'une action selon sa forme ; rien si Deepflow ne sait pas le montrer. */
export default function ActionPreview({ preview }) {
  const View = preview && VIEWS[preview.kind];
  return View ? <View preview={preview} /> : null;
}

ActionPreview.propTypes = { preview: PropTypes.object };

export const hasPreview = (preview) => Boolean(preview && VIEWS[preview.kind]);
